import { useSystemHealth, useRunHealthCheck, useToggleMaintenanceMode } from "@/hooks/useEventManagement";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { 
  Heart, 
  AlertTriangle, 
  XCircle, 
  Settings, 
  RefreshCw, 
  Clock,
  Activity,
  Zap
} from "lucide-react";
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";

const statusConfig = {
  healthy: {
    icon: Heart,
    color: "text-green-500",
    bgColor: "bg-green-50",
    variant: "default" as const,
    label: "Healthy"
  },
  warning: {
    icon: AlertTriangle,
    color: "text-yellow-500",
    bgColor: "bg-yellow-50",
    variant: "secondary" as const,
    label: "Warning"
  },
  critical: {
    icon: XCircle,
    color: "text-red-500",
    bgColor: "bg-red-50",
    variant: "destructive" as const,
    label: "Critical"
  },
  maintenance: {
    icon: Settings,
    color: "text-blue-500",
    bgColor: "bg-blue-50",
    variant: "outline" as const,
    label: "Maintenance"
  }
};

export function SystemHealthDashboard() {
  const { data: healthData, isLoading, refetch } = useSystemHealth();
  const runHealthCheck = useRunHealthCheck();
  const toggleMaintenanceMode = useToggleMaintenanceMode();
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  const handleRunHealthCheck = (serviceName?: string) => {
    runHealthCheck.mutate(serviceName);
  };

  const handleToggleMaintenanceMode = (enabled: boolean) => {
    setMaintenanceMode(enabled);
    toggleMaintenanceMode.mutate(enabled);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading System Health...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const healthyServices = healthData?.filter(s => s.status === 'healthy').length || 0;
  const totalServices = healthData?.length || 0;
  const overallHealthPercentage = totalServices > 0 ? (healthyServices / totalServices) * 100 : 0;

  const criticalServices = healthData?.filter(s => s.status === 'critical') || [];
  const warningServices = healthData?.filter(s => s.status === 'warning') || [];

  return (
    <div className="space-y-6">
      {/* Overall Health Summary */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overall Health</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overallHealthPercentage.toFixed(1)}%
            </div>
            <Progress value={overallHealthPercentage} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-2">
              {healthyServices} of {totalServices} services healthy
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critical Issues</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {criticalServices.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Services requiring immediate attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Warnings</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">
              {warningServices.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Services with performance concerns
            </p>
          </CardContent>
        </Card>
      </div>

      {/* System Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            System Controls
          </CardTitle>
          <CardDescription>
            Manual system operations and maintenance mode
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="font-medium">Maintenance Mode</p>
              <p className="text-sm text-muted-foreground">
                Temporarily disable system for maintenance
              </p>
            </div>
            <Switch
              checked={maintenanceMode}
              onCheckedChange={handleToggleMaintenanceMode}
              disabled={toggleMaintenanceMode.isPending}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="font-medium">Run Health Checks</p>
              <p className="text-sm text-muted-foreground">
                Perform immediate health check on all services
              </p>
            </div>
            <Button
              onClick={() => handleRunHealthCheck()}
              disabled={runHealthCheck.isPending}
              variant="outline"
            >
              {runHealthCheck.isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              Run Check
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Service Status Details */}
      <Card>
        <CardHeader>
          <CardTitle>Service Status</CardTitle>
          <CardDescription>
            Current status of all monitored services
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {healthData?.map((service) => {
              const config = statusConfig[service.status];
              const Icon = config.icon;
              
              return (
                <div key={service.id} className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-full ${config.bgColor}`}>
                      <Icon className={`h-4 w-4 ${config.color}`} />
                    </div>
                    <div>
                      <h4 className="font-medium">{service.service_name}</h4>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <span className="flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {service.response_time_ms}ms
                        </span>
                        <span>
                          Uptime: {service.uptime_percentage}%
                        </span>
                        <span>
                          Last check: {formatDistanceToNow(new Date(service.last_check), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Badge variant={config.variant}>
                      {config.label}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRunHealthCheck(service.service_name)}
                      disabled={runHealthCheck.isPending}
                    >
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}